/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useAgencyPermissions } from '@/features/agency';
import { useQueryClient } from '@tanstack/react-query';
import { useSalePaperworkMilestoneTypes } from '@features/sales/hooks/useSalePaperworkMilestoneTypes';
import { salesKeys } from '@features/sales/hooks/sales-keys';
import { milestoneLabel } from '@features/sales/constants/sale-states';
import type { SalePaperworkMilestoneType } from '@features/sales/types/sale.types';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { ListingTable, type ListingColumn } from '@/shared/ui/components/basic/listing-table';
import { NativeCheckbox, Text, TextInput, Title, UnstyledButton } from '@/shared/ui';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import {
  errorClasses, fieldsetClasses, labelClasses, submitBtnClasses,
} from './dialog-styles';

import { BlockMid, SettingsBlock } from './blocks';
import { BlockError, BlockLoading } from './BlockState';
import {
  SETTINGS_CARD, addBtnStyle, editBtnStyle, headerStyle, headerTextStyle,
  subtitleStyle, tableCaptionStyle, tableEmptyStyle, titleStyle,
} from './settings-block-styles';

/**
 * Which paperwork milestones a Sale in this agency may record a date
 * against. A fresh agency reads the four this backend used to hardcode
 * (ITBI, Escritura, Registro, Financiamento); adding or editing here never
 * removes or reorders anything — there is no action for either.
 *
 * @returns The paperwork milestones settings section.
 */
export function PaperworkMilestonesSection() {
  const { t } = useTranslation('features/agency-settings');
  const { t: tSales } = useTranslation('features/sales');
  const { agency } = useAgency((state) => state);
  const { canManageSalePaperworkMilestones } = useAgencyPermissions();
  const queryClient = useQueryClient();
  const { types, isLoading, isError, addType, updateType } = useSalePaperworkMilestoneTypes(agency.id ?? '');
  const [editingType, setEditingType] = useState<SalePaperworkMilestoneType | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const columns: ListingColumn<SalePaperworkMilestoneType>[] = [
    {
      key: 'label',
      label: t('v2.papelada.columnName'),
      width: '55%',
      sortable: true,
      sortAccessor: (type) => milestoneLabel(type, tSales),
      render: (type) => (
        <Text component="span" style={{ fontSize: 'var(--text-body-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
          {milestoneLabel(type, tSales)}
        </Text>
      ),
    },
    {
      key: 'required',
      label: t('v2.papelada.columnRequired'),
      width: '25%',
      sortable: true,
      sortType: 'number',
      sortAccessor: (type) => (type.required ? 1 : 0),
      render: (type) => (
        <Text component="span" style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>
          {type.required ? t('v2.papelada.requiredYes') : t('v2.papelada.requiredNo')}
        </Text>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '120px',
      align: 'right',
      render: (type) => {
        if (!canManageSalePaperworkMilestones) return null;
        return (
          <UnstyledButton onClick={() => setEditingType(type)} style={editBtnStyle}>
            {t('v2.profile.edit')}
          </UnstyledButton>
        );
      },
    },
  ];

  const caption = `${t('v2.papelada.count', { count: types.length })} · ${t('v2.papelada.explanation')}`;

  return (
    <SettingsBlock
      title={t('v2.papelada.title')}
      caption={caption}
      action={canManageSalePaperworkMilestones
        ? { label: t('v2.papelada.add'), onClick: () => setIsAdding(true), testId: 'paperwork-milestone-add' }
        : undefined}
      note={t('v2.papelada.note')}
      testId="paperwork-milestones-block"
    >
      {isLoading && <BlockLoading rows={4} />}
      {isError && (
        <BlockError onRetry={() => void queryClient.refetchQueries({ queryKey: salesKeys.milestoneTypes(agency.id ?? '') })} />
      )}
      {!isLoading && !isError && (
        <BlockMid>
          {types.length === 0
            ? <div style={tableEmptyStyle}>{t('v2.papelada.empty')}</div>
            : <ListingTable data={types} columns={columns} bare minWidth="0" testId="paperwork-milestone-types-table" />}
        </BlockMid>
      )}

      {isAdding && (
        <MilestoneTypeModal
          milestoneType={null}
          onClose={() => setIsAdding(false)}
          onSubmit={async (label, required) => {
            await addType(label, required);
            setIsAdding(false);
          }}
        />
      )}

      {editingType && (
        <MilestoneTypeModal
          milestoneType={editingType}
          onClose={() => setEditingType(null)}
          onSubmit={async (label, required) => {
            await updateType(editingType.id, { label, required });
            setEditingType(null);
          }}
        />
      )}
    </SettingsBlock>
  );
}

interface MilestoneTypeModalProps {
  /** Null when adding; the type being edited otherwise. */
  milestoneType: SalePaperworkMilestoneType | null;
  onClose: () => void;
  onSubmit: (label: string, required: boolean) => Promise<void>;
}

/**
 * Add/edit modal for one paperwork milestone type — plain local state, the
 * same convention `TeamSection`'s own forms use rather than a form library.
 *
 * Only ever mounted while open (the parent renders it conditionally), so
 * its local state always starts fresh for the type it was opened for —
 * no stale draft carried over from a previous edit.
 *
 * @returns The modal element.
 */
function MilestoneTypeModal({ milestoneType, onClose, onSubmit }: MilestoneTypeModalProps) {
  const { t } = useTranslation('features/agency-settings');
  const { t: tSales } = useTranslation('features/sales');
  const [label, setLabel] = useState(milestoneType ? milestoneLabel(milestoneType, tSales) : '');
  const [required, setRequired] = useState(milestoneType?.required ?? true);
  const [labelError, setLabelError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!label.trim()) {
      setLabelError(t('v2.papelada.labelRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(label.trim(), required);
      notifications.show({ color: 'green', message: t('v2.papelada.saved') });
    } catch {
      notifications.show({ color: 'red', message: t('v2.papelada.saveError') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdaptiveFormShell
      open
      onClose={onClose}
      title={milestoneType ? t('v2.papelada.editTitle') : t('v2.papelada.addTitle')}
      density="compact"
      footer={
        <UnstyledButton
          onClick={() => void handleSubmit()}
          disabled={submitting}
          className={submitBtnClasses}
          data-testid="milestone-modal-submit"
        >
          {submitting ? t('v2.papelada.saving') : t('v2.papelada.save')}
        </UnstyledButton>
      }
    >
      <div data-testid="milestone-form" className="flex flex-col gap-(--space-4)">
        <fieldset className={fieldsetClasses}>
          <Text as="label" htmlFor="milestone-label" className={labelClasses}>
            {t('v2.papelada.labelField')}
          </Text>
          <TextInput
            id="milestone-label"
            name="milestone-label"
            value={label}
            placeholder={t('v2.papelada.labelPlaceholder')}
            onChange={(e) => { setLabel(e.target.value); setLabelError(''); }}
            autoFocus
          />
          {labelError && <Text component="span" className={errorClasses}>{labelError}</Text>}
        </fieldset>

        <div className="flex items-center gap-(--space-2)">
          <NativeCheckbox
            id="milestone-required"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
          <Text as="label" htmlFor="milestone-required" className={labelClasses}>
            {t('v2.papelada.requiredField')}
          </Text>
        </div>
      </div>
    </AdaptiveFormShell>
  );
}

export default PaperworkMilestonesSection;
